import {
  BadgeVariant,
  AvatarVariant,
  AllFontColourOptions,
  AllStandardColourOptions,
} from '@preventx/oxygen';

// Types
import { PrismicColour } from '@/types/styling/colour';
import { PrismicFontColour } from '@/types/styling/font';
import { PrismicBadgeColours } from '@/types/styling/badge';
import { PrismicAvatarColours } from '@/types/styling/background';

type AvatarColourResponse = {
  variant?: AvatarVariant;
  colour: AllStandardColourOptions;
};

export const avatarColour = (
  colour: PrismicAvatarColours,
): AvatarColourResponse | null => {
  const configuration = {
    Default: null,

    // Normal Colours
    'Avatar Gray': {
      colour: 'gray',
    } satisfies AvatarColourResponse,
    'Avatar Red': {
      colour: 'red',
    } satisfies AvatarColourResponse,
    'Avatar Orange': {
      colour: 'orange',
    } satisfies AvatarColourResponse,
    'Avatar Amber': {
      colour: 'amber',
    } satisfies AvatarColourResponse,
    'Avatar Yellow': {
      colour: 'yellow',
    } satisfies AvatarColourResponse,
    'Avatar Lime': {
      colour: 'lime',
    } satisfies AvatarColourResponse,
    'Avatar Green': {
      colour: 'green',
    } satisfies AvatarColourResponse,
    'Avatar Teal': {
      colour: 'teal',
    } satisfies AvatarColourResponse,
    'Avatar Cyan': {
      colour: 'cyan',
    } satisfies AvatarColourResponse,
    'Avatar Sky': {
      colour: 'sky',
    } satisfies AvatarColourResponse,
    'Avatar Blue': {
      colour: 'blue',
    } satisfies AvatarColourResponse,
    'Avatar Indigo': {
      colour: 'indigo',
    } satisfies AvatarColourResponse,
    'Avatar Violet': {
      colour: 'violet',
    } satisfies AvatarColourResponse,
    'Avatar Purple': {
      colour: 'purple',
    } satisfies AvatarColourResponse,
    'Avatar Pink': {
      colour: 'pink',
    } satisfies AvatarColourResponse,

    // Solid Colours
    'Avatar Solid Gray': {
      colour: 'gray',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Red': {
      colour: 'red',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Orange': {
      colour: 'orange',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Amber': {
      colour: 'amber',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Yellow': {
      colour: 'yellow',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Lime': {
      colour: 'lime',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Green': {
      colour: 'green',
    } satisfies AvatarColourResponse,
    'Avatar Solid Teal': {
      colour: 'teal',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Cyan': {
      colour: 'cyan',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Sky': {
      colour: 'sky',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Blue': {
      colour: 'blue',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Indigo': {
      colour: 'indigo',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Violet': {
      colour: 'violet',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Purple': {
      colour: 'purple',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Pink': {
      colour: 'pink',
      variant: 'solid',
    } satisfies AvatarColourResponse,

    // Semantic Colours
    'Avatar Negative': {
      colour: 'negative',
    } satisfies AvatarColourResponse,
    'Avatar Notice': {
      colour: 'notice',
    } satisfies AvatarColourResponse,
    'Avatar Primary': {
      colour: 'primary',
    } satisfies AvatarColourResponse,
    'Avatar Positive': {
      colour: 'positive',
    } satisfies AvatarColourResponse,
    'Avatar Informative': {
      colour: 'informative',
    } satisfies AvatarColourResponse,

    'Avatar Solid Negative': {
      variant: 'solid',
      colour: 'negative',
    } satisfies AvatarColourResponse,
    'Avatar Solid Notice': {
      colour: 'notice',
      variant: 'solid',
    } satisfies AvatarColourResponse,
    'Avatar Solid Primary': {
      variant: 'solid',
      colour: 'primary',
    } satisfies AvatarColourResponse,
    'Avatar Solid Positive': {
      variant: 'solid',
      colour: 'positive',
    } satisfies AvatarColourResponse,
    'Avatar Solid Informative': {
      variant: 'solid',
      colour: 'informative',
    } satisfies AvatarColourResponse,
  }[colour];

  return configuration ?? null;
};

type BadgeColourResponse = {
  variant?: BadgeVariant;
  colour: AllStandardColourOptions;
};

export const badgeColour = (
  colour: PrismicBadgeColours,
): BadgeColourResponse | null => {
  const configuration = {
    Default: null,

    // Normal Colours
    'Badge - Gray': {
      colour: 'gray',
    } satisfies BadgeColourResponse,
    'Badge - Red': {
      colour: 'red',
    } satisfies BadgeColourResponse,
    'Badge - Orange': {
      colour: 'orange',
    } satisfies BadgeColourResponse,
    'Badge - Amber': {
      colour: 'amber',
    } satisfies BadgeColourResponse,
    'Badge - Yellow': {
      colour: 'yellow',
    } satisfies BadgeColourResponse,
    'Badge - Lime': {
      colour: 'lime',
    } satisfies BadgeColourResponse,
    'Badge - Green': {
      colour: 'green',
    } satisfies BadgeColourResponse,
    'Badge - Teal': {
      colour: 'teal',
    } satisfies BadgeColourResponse,
    'Badge - Cyan': {
      colour: 'cyan',
    } satisfies BadgeColourResponse,
    'Badge - Sky': {
      colour: 'sky',
    } satisfies BadgeColourResponse,
    'Badge - Blue': {
      colour: 'blue',
    } satisfies BadgeColourResponse,
    'Badge - Indigo': {
      colour: 'indigo',
    } satisfies BadgeColourResponse,
    'Badge - Violet': {
      colour: 'violet',
    } satisfies BadgeColourResponse,
    'Badge - Purple': {
      colour: 'purple',
    } satisfies BadgeColourResponse,
    'Badge - Pink': {
      colour: 'pink',
    } satisfies BadgeColourResponse,

    // Solid Colours
    'Badge - Solid Gray': {
      colour: 'gray',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Red': {
      colour: 'red',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Orange': {
      colour: 'orange',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Amber': {
      colour: 'amber',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Yellow': {
      colour: 'yellow',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Lime': {
      colour: 'lime',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Green': {
      colour: 'green',
    } satisfies BadgeColourResponse,
    'Badge - Solid Teal': {
      colour: 'teal',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Cyan': {
      colour: 'cyan',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Sky': {
      colour: 'sky',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Blue': {
      colour: 'blue',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Indigo': {
      colour: 'indigo',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Violet': {
      colour: 'violet',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Purple': {
      colour: 'purple',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge - Solid Pink': {
      colour: 'pink',
      variant: 'solid',
    } satisfies BadgeColourResponse,

    // Semantic Colours
    'Badge Semantic - Negative': {
      colour: 'negative',
    } satisfies BadgeColourResponse,
    'Badge Semantic - Notice': {
      colour: 'notice',
    } satisfies BadgeColourResponse,
    'Badge Semantic - Primary': {
      colour: 'primary',
    } satisfies BadgeColourResponse,
    'Badge Semantic - Positive': {
      colour: 'positive',
    } satisfies BadgeColourResponse,
    'Badge Semantic - Informative': {
      colour: 'informative',
    } satisfies BadgeColourResponse,

    'Badge Semantic - Solid Negative': {
      variant: 'solid',
      colour: 'negative',
    } satisfies BadgeColourResponse,
    'Badge Semantic - Solid Notice': {
      colour: 'notice',
      variant: 'solid',
    } satisfies BadgeColourResponse,
    'Badge Semantic - Solid Primary': {
      variant: 'solid',
      colour: 'primary',
    } satisfies BadgeColourResponse,
    'Badge Semantic - Solid Positive': {
      variant: 'solid',
      colour: 'positive',
    } satisfies BadgeColourResponse,
    'Badge Semantic - Solid Informative': {
      variant: 'solid',
      colour: 'informative',
    } satisfies BadgeColourResponse,
  }[colour];

  return configuration ?? null;
};

export const fontColour = (
  colour: PrismicFontColour,
): AllFontColourOptions | null => {
  const configuration = {
    Default: null,

    Sky: 't-sky' satisfies AllFontColourOptions,
    Red: 't-red' satisfies AllFontColourOptions,
    Lime: 't-lime' satisfies AllFontColourOptions,
    Teal: 't-teal' satisfies AllFontColourOptions,
    Blue: 't-blue' satisfies AllFontColourOptions,
    Cyan: 't-cyan' satisfies AllFontColourOptions,
    Gray: 't-gray' satisfies AllFontColourOptions,
    Pink: 't-pink' satisfies AllFontColourOptions,
    Amber: 't-amber' satisfies AllFontColourOptions,
    Green: 't-green' satisfies AllFontColourOptions,
    Indigo: 't-indigo' satisfies AllFontColourOptions,
    Orange: 't-orange' satisfies AllFontColourOptions,
    Purple: 't-purple' satisfies AllFontColourOptions,
    Violet: 't-violet' satisfies AllFontColourOptions,
    Yellow: 't-yellow' satisfies AllFontColourOptions,
  }[colour] as AllFontColourOptions | null;

  return configuration ?? null;
};

export const colour = (
  colour: PrismicColour,
): AllStandardColourOptions | null => {
  const colourScheme = {
    Default: null,
    Red: 'red' satisfies AllStandardColourOptions,
    Sky: 'sky' satisfies AllStandardColourOptions,
    Gray: 'gray' satisfies AllStandardColourOptions,
    Lime: 'lime' satisfies AllStandardColourOptions,
    Mint: 'mint' satisfies AllStandardColourOptions,
    Ruby: 'ruby' satisfies AllStandardColourOptions,
    Pink: 'pink' satisfies AllStandardColourOptions,
    Plum: 'plum' satisfies AllStandardColourOptions,
    Blue: 'blue' satisfies AllStandardColourOptions,
    Teal: 'teal' satisfies AllStandardColourOptions,
    Gold: 'gold' satisfies AllStandardColourOptions,
    Iris: 'iris' satisfies AllStandardColourOptions,
    Cyan: 'cyan' satisfies AllStandardColourOptions,
    Jade: 'jade' satisfies AllStandardColourOptions,
    Green: 'green' satisfies AllStandardColourOptions,
    Brown: 'brown' satisfies AllStandardColourOptions,
    Amber: 'amber' satisfies AllStandardColourOptions,
    Grass: 'grass' satisfies AllStandardColourOptions,
    Violet: 'violet' satisfies AllStandardColourOptions,
    Indigo: 'indigo' satisfies AllStandardColourOptions,
    Yellow: 'yellow' satisfies AllStandardColourOptions,
    Purple: 'purple' satisfies AllStandardColourOptions,
    Bronze: 'bronze' satisfies AllStandardColourOptions,
    Orange: 'orange' satisfies AllStandardColourOptions,
    Tomato: 'tomato' satisfies AllStandardColourOptions,
    Crimson: 'crimson' satisfies AllStandardColourOptions,
    Notice: 'notice' satisfies AllStandardColourOptions,
    Primary: 'primary' satisfies AllStandardColourOptions,
    Positive: 'positive' satisfies AllStandardColourOptions,
    Negative: 'negative' satisfies AllStandardColourOptions,
    Informative: 'informative' satisfies AllStandardColourOptions,
    'Brand 1': 'brand-1' satisfies AllStandardColourOptions,
    'Brand 2': 'brand-2' satisfies AllStandardColourOptions,
    'Brand 3': 'brand-3' satisfies AllStandardColourOptions,
    'Brand 4': 'brand-4' satisfies AllStandardColourOptions,
    'Brand 5': 'brand-5' satisfies AllStandardColourOptions,
    'Brand 6': 'brand-6' satisfies AllStandardColourOptions,
    'Brand 7': 'brand-7' satisfies AllStandardColourOptions,
    'Brand 8': 'brand-8' satisfies AllStandardColourOptions,
    'Brand 9': 'brand-9' satisfies AllStandardColourOptions,
    'Brand 10': 'brand-10' satisfies AllStandardColourOptions,
  }[colour] as AllStandardColourOptions | null;

  return colourScheme;
};
